import { ExperienceLayouts } from '@/types/gql.generated';
import type { TemplateLayout } from '../printableConfigAdapter';
import type { TemplateRenderer } from '../types';

type LayoutKey = TemplateLayout | 'default';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRenderer = TemplateRenderer<any>;

const rendererLoaders: Record<LayoutKey, () => Promise<AnyRenderer>> = {
  default: () =>
    import('./listVertical/render').then((m) => m.listVerticalRenderer),
  printableListVertical: () =>
    import('./listVertical/render').then((m) => m.listVerticalRenderer),
  // Classic (flag-off) fallback for the Printable List is the vertical list.
  printableList: () =>
    import('./listVertical/render').then((m) => m.listVerticalRenderer),
  printableYearlyMonthCard: () =>
    import('./yearlyMonthCard/render').then((m) => m.yearlyMonthCardRenderer),
  printableYearly3BoxMonthCard: () =>
    import('./yearly3BoxMonthCard/render').then(
      (m) => m.yearly3BoxMonthCardRenderer
    ),
  printableYearly6BoxMonthCard: () =>
    import('./yearly6BoxMonthCard/render').then(
      (m) => m.yearly6BoxMonthCardRenderer
    ),
  printableWeek: () => import('./week/render').then((m) => m.weekRenderer),
  printableCalendar: () =>
    import('./calendar/render').then((m) => m.calendarRenderer),
  plannerCalendar: () =>
    import('./plannerCalendar/render').then((m) => m.plannerMonthRenderer),
  plannerMonth: () =>
    import('./plannerMonth/render').then((m) => m.plannerMonthRenderer),
  plannerWeek: () =>
    import('./plannerWeek/render').then((m) => m.plannerWeekRenderer),
  // Block-only: there is no classic template to fall back to.
  dayByCategory: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.DayByCategory]!
    ),
  // Rendered by CustomArtifactPreview, never through a template; listed only
  // so the Record stays exhaustive.
  printableCustom: () =>
    import('./listVertical/render').then((m) => m.listVerticalRenderer),
};

// Block-component variants: same group/paginate pipeline with the Page swapped
// for the block-composed page scaffolding in features/layouts. Selected by the
// printableBlockEnabled feature switch. The planner registry is keyed by the
// experience layoutId; each entry is a Page paired with its collection module.
const blockRendererLoaders: Partial<
  Record<LayoutKey, () => Promise<AnyRenderer>>
> = {
  // plannerCalendar is the earlier month printable layout; under the block
  // variant it renders through the same block month page as plannerMonth
  // (mirroring the classic loaders, where both resolve month renderers).
  plannerCalendar: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.PlannerMonth]!
    ),
  plannerMonth: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.PlannerMonth]!
    ),
  plannerWeek: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.PlannerWeek]!
    ),
  printableWeek: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.PrintableWeek]!
    ),
  dayByCategory: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.DayByCategory]!
    ),
  // The Printable List renders through the block list page + PrintableList
  // collection under the flag; classic fallback is listVertical (rendererLoaders).
  printableList: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.PrintableList]!
    ),
  // The month-card printables (3-box / 6-box / 12-box) render through the block
  // MonthCardsPage + MonthCardGrid collection under the flag; the classic
  // yearlyMonthCard template stays the flag-off fallback. All three share one
  // MonthCardGrid module (see PLANNER_RENDERERS), with the grid's row count derived
  // from the experience layoutId. All three carry a real MonthCardGrid collection
  // block in their server layout definitions and are in BLOCK_PREVIEW_LAYOUT_IDS,
  // so both the committed child and the add-view preview get their blocks the same
  // way plannerMonth / plannerWeek do — no client-side block remap.
  printableYearlyMonthCard: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) => m.PLANNER_RENDERERS[ExperienceLayouts.PrintableYearlyMonthCard]!
    ),
  printableYearly3BoxMonthCard: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) =>
        m.PLANNER_RENDERERS[ExperienceLayouts.PrintableYearly3BoxMonthCard]!
    ),
  printableYearly6BoxMonthCard: () =>
    import('@/features/layouts/page/PlannerBlockLayout/plannerModules').then(
      (m) =>
        m.PLANNER_RENDERERS[ExperienceLayouts.PrintableYearly6BoxMonthCard]!
    ),
};

export const hasBlockTemplateRenderer = (layout: TemplateLayout): boolean =>
  Boolean(blockRendererLoaders[layout]);

export type TemplateRendererOptions = {
  printableBlockEnabled?: boolean;
};

export const loadTemplateRenderer = (
  layout: TemplateLayout,
  options?: TemplateRendererOptions
): Promise<AnyRenderer> => {
  const blockLoader = options?.printableBlockEnabled
    ? blockRendererLoaders[layout]
    : undefined;
  return blockLoader
    ? blockLoader()
    : (rendererLoaders[layout] ?? rendererLoaders.default)();
};
